import React, { useCallback, useEffect, useMemo } from 'react'
import { Field, useFormikContext } from 'formik'
import { addDays, isAfter, isSameDay, isWeekend } from 'date-fns'
import * as yup from 'yup'
import { DateTimePicker } from 'components'
import { NewBooking } from 'api/types'
import {
  getBookingEndDateMin,
  getBookingEndDateMax,
} from 'config/bookingLimits'

import { getNationalHolidays } from 'utils/date'
import { StationField } from './StationField'
import { useTranslation } from 'react-i18next'
import { formatHtmlDateInput } from 'utils/format'

export const testWeekend = (date?: Date | null): boolean => {
  return date ? !isWeekend(date) : true
}

const getHoliday = (date: Date) =>
  getNationalHolidays(date).find(
    ([holiday, _name]) =>
      date.getDate() === holiday.getDate() &&
      date.getMonth() === holiday.getMonth()
  )

export const testHoliday = (date?: Date | null): boolean =>
  date ? !getHoliday(date) : true

export const testTime = (date?: Date | null): boolean => {
  if (date) {
    return (
      date.getHours() +
        date.getMinutes() +
        date.getSeconds() +
        date.getMilliseconds() !==
      0
    )
  } else {
    return false
  }
}

export const calcMinDate = (date: Date, fromNow = 4): Date => {
  if (isWeekend(date) || getHoliday(date)) {
    return calcMinDate(addDays(date, 1), fromNow)
  }

  if (fromNow > 0) {
    return calcMinDate(addDays(date, 1), fromNow - 1)
  } else {
    return date
  }
}

const testWorkdays = (date?: Date | null): boolean => {
  const minDate = calcMinDate(new Date(Date.now()))
  return date ? isSameDay(date, minDate) || isAfter(date, minDate) : true
}

export const defaultDates = (time: any) => {
  const date = new Date(Date.now())
  const minStartDate = calcMinDate(date)
  new Date(date.setDate(date.getDate() + 1))
  const minEndDate = new Date(2026, 2, 16)
  return {
    defaultStartDate: `${minStartDate.getFullYear()}-${(
      minStartDate.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}-${minStartDate
      .getDate()
      .toString()
      .padStart(2, '0')}T${time}`,
    defaultEndDate: `${minEndDate.getFullYear()}-${(minEndDate.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${minEndDate
      .getDate()
      .toString()
      .padStart(2, '0')}T${time}`,
  }
}

const BOOKING_DEADLINE = new Date(2026, 2, 16) // month index is zero-based
export const maxStartDate = (): Date => {
  return BOOKING_DEADLINE
}

export const maxEndDate = (): Date => {
  const maxEndDate = BOOKING_DEADLINE
  maxEndDate.setHours(23, 59, 59)
  return maxEndDate
}

export const validationSchema = yup.object({
  startDate: yup
    .date()
    .required(
      'pages.bookings.bookingForm.bookingFields.validation.startDate.required'
    )
    .test(
      'min-workdays',
      'pages.bookings.bookingForm.bookingFields.validation.startDate.test',
      testWorkdays
    )
    .min(
      new Date('2020-10-01T00:00:00.000'),
      'pages.bookings.bookingForm.bookingFields.validation.startDate.minDate'
    )
    .test(
      'not-weekend',
      'pages.bookings.bookingForm.bookingFields.validation.startDate.weekendTest',
      testWeekend
    )
    .test(
      'not-holiday',
      'pages.bookings.bookingForm.bookingFields.validation.startDate.holidayTest',
      testHoliday
    )
    .test(
      'has-time',
      'pages.bookings.bookingForm.bookingFields.validation.startDate.required',
      testTime
    ),
  endDate: yup
    .date()
    .required(
      'pages.bookings.bookingForm.bookingFields.validation.endDate.required'
    )
    .test(
      'not-weekend',
      'pages.bookings.bookingForm.bookingFields.validation.endDate.weekendTest',
      testWeekend
    )
    .test(
      'not-holiday',
      'pages.bookings.bookingForm.bookingFields.validation.endDate.holidayTest',
      testHoliday
    )
    .test(
      'has-time',
      'pages.bookings.bookingForm.bookingFields.validation.endDate.required',
      testTime
    )
    /*.when('startDate', (startDate: Date, schema: yup.DateSchema) => {
      const minDate = new Date(startDate)
      if (!startDate || !minDate) {
        return schema
      }
      minDate.setHours(0, 0, 0, 0)
      minDate.setDate(minDate.getDate() + 1)
      return schema.min(
        minDate,
        'pages.bookings.bookingForm.bookingFields.validation.endDate.minDate'
      )
    })
    .when('startDate', (startDate: Date, schema: yup.DateSchema) => {
      if (!startDate) {
        return schema
      }
      const maxDate = maxEndDate(startDate)
      return schema.max(
        maxDate,
        'pages.bookings.bookingForm.bookingFields.validation.endDate.maxDate'
      )
    })*/
    .test(
      'min-date',
      'pages.bookings.bookingForm.bookingFields.validation.endDate.minDate',
      (value) => {
        if (window.REACT_APP_DISABLE_BOOKING_LIMITS) return true
        return value ? value >= getBookingEndDateMin() : true
      }
    )
    .test(
      'max-date',
      'pages.bookings.bookingForm.bookingFields.validation.endDate.maxDate',
      (value) => {
        if (window.REACT_APP_DISABLE_BOOKING_LIMITS) return true
        return value ? value <= getBookingEndDateMax() : true
      }
    ),
  pickUpStationId: yup
    .string()
    .required(
      'pages.bookings.bookingForm.bookingFields.validation.pickUpStationId.required'
    ),
  dropOffStationId: yup.string(),
})

interface BookingFieldsProps {
  setVehicleDates: ({
    startDate,
    endDate,
  }: {
    startDate: string
    endDate: string
  }) => void
}

export const BookingFields: React.FunctionComponent<BookingFieldsProps> = ({
  setVehicleDates,
}) => {
  const times = useMemo(
    () => [
      {
        value: '09:00:00',
        label: 'pages.bookings.bookingForm.bookingFields.times.t0900',
      },
      {
        value: '10:00:00',
        label: 'pages.bookings.bookingForm.bookingFields.times.t1000',
      },
      {
        value: '11:00:00',
        label: 'pages.bookings.bookingForm.bookingFields.times.t1100',
      },
      {
        value: '12:00:00',
        label: 'pages.bookings.bookingForm.bookingFields.times.t1200',
      },
      {
        value: '13:00:00',
        label: 'pages.bookings.bookingForm.bookingFields.times.t1300',
      },
      {
        value: '14:00:00',
        label: 'pages.bookings.bookingForm.bookingFields.times.t1400',
      },
      {
        value: '15:00:00',
        label: 'pages.bookings.bookingForm.bookingFields.times.t1500',
      },
      {
        value: '16:00:00',
        label: 'pages.bookings.bookingForm.bookingFields.times.t1600',
      },
    ],
    []
  )
  const { t } = useTranslation()
  const { values: booking, setFieldValue } = useFormikContext<NewBooking>()
  const { defaultStartDate, defaultEndDate } = defaultDates(times[1].value)

  useEffect(() => {
    setVehicleDates({
      startDate: booking.startDate,
      endDate: booking.endDate,
    })
  }, [setVehicleDates, booking.startDate, booking.endDate])

  useEffect(() => {
    if (booking.startDate === '') {
      setFieldValue('startDate', defaultStartDate)
    }
    if (booking.endDate === '') {
      setFieldValue('endDate', defaultEndDate)
    }
  }, [
    defaultStartDate,
    defaultEndDate,
    booking.startDate,
    booking.endDate,
    setFieldValue,
    times,
  ])
  const setStation = useCallback(
    (stationId: string) => {
      setFieldValue('pickUpStationId', stationId)
      setFieldValue('dropOffStationId', stationId)
    },
    [setFieldValue]
  )

  useEffect(() => {
    if (booking.dropOffStationId !== booking.pickUpStationId) {
      setFieldValue('dropOffStationId', booking.pickUpStationId)
    }
  }, [setFieldValue, booking])

  return (
    <>
      <Field
        component={DateTimePicker}
        id="startDate"
        name="startDate"
        dateLabel={t('pages.bookings.bookingForm.bookingFields.startDate')}
        datePattern="\d{2}\.\d{2}\.\d{4}"
        timeLabel={t('pages.bookings.bookingForm.bookingFields.startTime')}
        timeOptions={times}
        placeholder="DD.MM.YYYY"
        min="2020-10-01"
        {...(!window.REACT_APP_DISABLE_BOOKING_LIMITS && {
          max: formatHtmlDateInput(maxStartDate()),
        })}
        required
        defaultValue={defaultStartDate}
      />
      <Field
        component={DateTimePicker}
        id="endDate"
        name="endDate"
        dateLabel={t('pages.bookings.bookingForm.bookingFields.endDate')}
        datePattern="\d{2}\.\d{2}\.\d{4}"
        timeLabel={t('pages.bookings.bookingForm.bookingFields.endTime')}
        timeOptions={times}
        placeholder="DD.MM.YYYY"
        min="2026-03-16"
        {...(!window.REACT_APP_DISABLE_BOOKING_LIMITS && {
          max: formatHtmlDateInput(maxEndDate()),
        })}
        required
        defaultValue={defaultEndDate}
      />

      <StationField booking={booking} setStation={setStation} />
    </>
  )
}
